import _ from 'lodash';
import { sqWorkbenchStore } from '@/core/core.stores';
import { LAST_DITCH_LAYOUT } from '@/impactReports/impactReport.types';
import {
  fetchCategories,
  getNominationLabel,
  getTableColumnDefinition,
  getWorksheetLabels,
} from '@/impactReports/impactReport.utilities';
import {
  getKPIColumnNameFromExpectedAnnual,
  getKPIExpectedAnnualColumnName,
  isKPIColumnName,
  isKPIExpectedAnnualColumnName,
} from '@/impactReports/impactReportDisplay.utilities';
import {
  fetchImpactReportLayouts,
  findTableDefinitionIdByName,
  parseImpactReportColumnDefinitions,
  parseImpactReportFilterModel,
  parseImpactReportLayoutJson,
  parseImpactReportMapZoom,
} from '@/impactReports/impactReportLayout.utilities';
import { useImpactReportActions } from '@/impactReports/impactReports.store';
import { getImpactReportByWorksheet } from '@/impactReports/tiles/table.utilities';
import { setImpactReportFavoriteLayoutId } from '@/workbench/workbench.actions';
import { sqContextApi } from '@seeqdev/api/endpoint/ContextApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionBulkEditInputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { FlaggedItem, ImpactReportLayout } from '@/impactReports/impactReport.types';

const findLayoutById = (layouts: ImpactReportLayout[], id?: string) =>
  id ? layouts.find((layout) => layout.id === id) : undefined;

const ensureFavoriteLayoutId = (
  layouts: ImpactReportLayout[],
  defaultLayout: ImpactReportLayout | undefined,
): string | undefined => {
  const favoriteLayoutId = sqWorkbenchStore.impactReportFavoriteLayoutId;
  const favoriteLayout = findLayoutById(layouts, favoriteLayoutId);
  if (favoriteLayout) return favoriteLayoutId;

  const fallbackId = defaultLayout?.id ?? layouts[0]?.id;
  if (fallbackId && favoriteLayoutId !== fallbackId) {
    setImpactReportFavoriteLayoutId(fallbackId);
  }
  return fallbackId;
};

export const useImpactReportApiActions = () => {
  const {
    setCategories,
    setLayouts,
    setColumnConfiguration,
    setMapZoom,
    setFilterModel,
    setLayoutDefinition,
    setSelectedLayoutId,
    setTableId,
    setKpiColumns,
    setExpectedAnnualKpiColumns,
    setExpectedAnnualKpiVisibility,
    setShowSite,
    setShowAssetPath,
    setShowLocation,
  } = useImpactReportActions();

  const loadImpactReportCategories = async () => {
    const categories = await fetchCategories();
    setCategories(categories);
  };

  const initializeImpactReportLayouts = async () => {
    try {
      const layouts = await fetchImpactReportLayouts();
      setLayouts(layouts);

      const defaultLayout = layouts.find((layout) => layout.isDefault);
      const favoriteLayoutId = ensureFavoriteLayoutId(layouts, defaultLayout);

      const applyLayoutSettings = (layout?: ImpactReportLayout) => {
        const columns = parseImpactReportColumnDefinitions(layout?.columnDefinitions);
        if (columns) setColumnConfiguration(columns);

        setMapZoom(parseImpactReportMapZoom(layout?.mapZoom));
        setFilterModel(parseImpactReportFilterModel(layout?.filterModel));
      };

      const applyLayout = (layout?: ImpactReportLayout) => {
        // layout is complicated :sweat:
        // if we are requesting a specific layout the user has selected an option in the dropdown - if we don't have a
        // layout we will fall back to the default layout. we really should never end up without any layout
        const parsed =
          layout && !layout.isDefault && layout.layoutJson
            ? parseImpactReportLayoutJson(layout.layoutJson)
            : parseImpactReportLayoutJson(defaultLayout?.layoutJson ?? null);

        setLayoutDefinition(parsed ?? LAST_DITCH_LAYOUT);
        applyLayoutSettings(layout);
      };

      // Favorite is the default layout
      if (favoriteLayoutId === SeeqNames.ImpactReport.LayoutDefaultDatumId) {
        setSelectedLayoutId(SeeqNames.ImpactReport.LayoutDefaultDatumId);
        applyLayout(defaultLayout);
        return;
      }

      // Favorite layout exists
      const favoriteLayout = findLayoutById(layouts, favoriteLayoutId);

      if (favoriteLayout) {
        setSelectedLayoutId(favoriteLayout.id);
        applyLayout(favoriteLayout);
        return;
      }

      // Fallback to default
      setSelectedLayoutId(defaultLayout?.id ?? SeeqNames.ImpactReport.LayoutDefaultDatumId);
      applyLayout(defaultLayout);
    } catch (error) {
      console.error('Failed to initialize Impact Report layouts', error);
    }
  };

  /**
   * The Impact Report Table description field is used to store configuration options that apply application wide to
   * the Impact Report Table.
   * For now we save the configuration options for showing the Site and Asset Path columns in the table (and the form)
   */
  const loadImpactTableConfiguration = async (tableId: string) => {
    const tableResponse = await sqTableDefinitionsApi.getTableDefinition({ id: tableId });
    let defaultExpectedAnnualVisibility: Record<string, boolean> = {};
    if (tableResponse.data.columnDefinitions) {
      const kpiColumns = tableResponse.data.columnDefinitions.filter((column) => isKPIColumnName(column.columnName));
      const expectedAnnualKpiColumns = tableResponse.data.columnDefinitions.filter((column) =>
        isKPIExpectedAnnualColumnName(column.columnName),
      );
      setKpiColumns(kpiColumns);
      setExpectedAnnualKpiColumns(expectedAnnualKpiColumns);
      const expectedAnnualBases = new Set(
        expectedAnnualKpiColumns.map((column) => getKPIColumnNameFromExpectedAnnual(column.columnName)),
      );
      defaultExpectedAnnualVisibility = kpiColumns.reduce(
        (acc, column) => {
          acc[column.columnName] = expectedAnnualBases.has(column.columnName);
          return acc;
        },
        {} as Record<string, boolean>,
      );
    }

    if (!tableResponse.data.description || tableResponse.data.description === 'Impact Report Table') {
      if (Object.keys(defaultExpectedAnnualVisibility).length) {
        setExpectedAnnualKpiVisibility(defaultExpectedAnnualVisibility);
      }
    }

    if (!tableResponse.data.description || tableResponse.data.description === 'Impact Report Table') return;
    const { showSite, showAssetPath, showLocation, expectedAnnualKpiVisibility } =
      tableResponse.data.description && JSON.parse(tableResponse.data.description);
    setShowSite(showSite);
    setShowAssetPath(showAssetPath);
    setShowLocation(showLocation);
    setExpectedAnnualKpiVisibility(expectedAnnualKpiVisibility ?? defaultExpectedAnnualVisibility);

    return { showSite, showAssetPath, showLocation, expectedAnnualKpiVisibility };
  };

  /**
   * Fetches the Impact Report Table ID from the server
   * If the table does not exist, it creates it
   */
  const loadImpactReportTableId = async () => {
    const tableId = await findTableDefinitionIdByName(SeeqNames.ImpactReport.ImpactTableName);
    if (!tableId) return undefined;
    await loadImpactTableConfiguration(tableId);
    setTableId(tableId);
    return tableId;
  };

  const loadImpactReportForWorksheet = async (
    workbookId: string | undefined,
    worksheetId: string | undefined,
    tableId: string | undefined,
  ) => {
    if (workbookId && worksheetId) {
      const resolvedTableId = tableId ?? (await loadImpactReportTableId());
      if (!resolvedTableId) return undefined;
      return getImpactReportByWorksheet(resolvedTableId, worksheetId, workbookId);
    }

    return undefined;
  };

  /**
   * This function fetches all the pages that have been nominated and are displayed as "Impact Report Nominations" in
   * the application.
   *
   * Remember the "hierarchy" of the context labels:
   * - the Category is the "parent" of the Label
   * - the Label gets then applied as a Context Label
   * - the ID of that context label is what identifies the individual nomination label
   *
   * We also only display nominations to the owners of the workbook (yes -worksheets are impact reports not workbooks,
   * but worksheets inherit their permissions from the workbook - so we need to filter them by workbook owner.
   */
  const getNominatedWorksheets = async (): Promise<FlaggedItem[]> => {
    // first we have to fetch the "prompt" label (the one that tells us the report was nominated) so we can use its
    // id in the next query:
    const promptLabel = await getNominationLabel();
    const tableId = await loadImpactReportTableId();
    if (!promptLabel || !tableId) return [];
    // fetch all the items that the "prompt" label was applied to
    const { data: labeledItemsResponse } = await sqContextApi.getLabeledItems({ labelId: promptLabel.id! });
    if (!labeledItemsResponse.items) return [];

    // fetch the full worksheet and workbook data for each item - ideally we'd get that all from the query above but
    // this works for now
    // we need to fetch the owner of the workbook to filter out the flagged worksheets that are not owned by the current
    const allFlaggedItems = await Promise.all(
      labeledItemsResponse.items.map(async (item) => {
        const { data: worksheet } = await sqItemsApi.getItemAndAllProperties({ id: item.id! });
        const workbookId = worksheet.workbookId;
        if (!workbookId) {
          return;
        }
        const { data: workbook } = await sqWorkbooksApi.getWorkbook({ id: workbookId });

        if (workbook?.owner?.id === sqWorkbenchStore.currentUser?.id) {
          return {
            name: `${workbook.name} - ${worksheet.name}`,
            workbookId,
            worksheetId: item.id!,
            owner: workbook.owner?.id,
          };
        }
      }),
    );

    // now we can filter out all the flagged worksheets that are not owned by the current user
    const candidates: (FlaggedItem | undefined)[] = allFlaggedItems.filter(
      (item) => item && item.owner === sqWorkbenchStore.currentUser?.id,
    );
    // and finally we check if the worksheet is already an impact report
    const finalists = await Promise.all(
      candidates.map(async (candidate) => {
        if (!candidate) return null;
        const existing = await getImpactReportByWorksheet(tableId, candidate.worksheetId, candidate.workbookId!);
        if (existing) {
          return null;
        }
        const labelId: string = _.first(await getWorksheetLabels(candidate.worksheetId))!; // this is the id of the applied label
        return { ...candidate, labelId };
      }),
    );
    // we're done :) now we have a list of all nominated worksheets that are owned by the current user not impact
    // reports already
    return _.compact(finalists);
  };

  /**
   * kpiColumName is the name of the column that will be added to the table - it contains a prefix and a postfix that
   * encapsulates the unit of measure => use the helpers to extract those if needed!
   */
  const addCustomQuantifierColumnToImpactReportTable = async (
    kpiColumName: string,
    description?: string,
    includeExpectedAnnualSavings = false,
  ) => {
    const columnDefinitions = [getTableColumnDefinition(kpiColumName, ColumnTypeEnum.TEXT, false, false, description)];
    if (includeExpectedAnnualSavings) {
      columnDefinitions.push(
        getTableColumnDefinition(getKPIExpectedAnnualColumnName(kpiColumName), ColumnTypeEnum.TEXT),
      );
    }
    const tableId = await loadImpactReportTableId();
    if (!tableId) return;
    await sqTableDefinitionsApi.addColumnsToTableDefinition({ columnDefinitions }, { id: tableId });
  };

  const addExpectedAnnualSavingsColumnToImpactReportTable = async (expectedAnnualColumnName: string) => {
    const columnDefinitions = [getTableColumnDefinition(expectedAnnualColumnName, ColumnTypeEnum.TEXT)];
    const tableId = await loadImpactReportTableId();
    if (!tableId) return;
    await sqTableDefinitionsApi.addColumnsToTableDefinition({ columnDefinitions }, { id: tableId });
  };

  return {
    loadImpactReportCategories,
    initializeImpactReportLayouts,
    loadImpactTableConfiguration,
    loadImpactReportTableId,
    loadImpactReportForWorksheet,
    getNominatedWorksheets,
    addCustomQuantifierColumnToImpactReportTable,
    addExpectedAnnualSavingsColumnToImpactReportTable,
  };
};
